import { GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport'
import { Deferred, UnaryCall, type RpcMetadata, type RpcStatus } from '@protobuf-ts/runtime-rpc'
import { API_BASE_URL } from '@/modules/constants/Environments'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { resolveGrpcUrls, activeGrpcUrl, setActiveGrpcUrl } from '@/utils/resolveApiUrls'

/**
 * Проверяет, является ли ошибка сетевым сбоем или недоступностью сервера/шлюза
 *
 * @param err - перехваченная ошибка вызова
 * @returns true если ошибка вызвана недоступностью удалённого узла сети
 */
function isNetworkOrUnavailableError(err: unknown): boolean {
  if (!err) return false

  const errorObj = err as {
    name?: string
    message?: string
    code?: string | number
    status?: number
  }

  const msg = String(errorObj.message || err).toLowerCase()
  const code = String(errorObj.code || '').toUpperCase()

  // 1. TypeError: Failed to fetch (стандартный сбой fetch в браузере)
  if (
    errorObj.name === 'TypeError' ||
    msg.includes('failed to fetch') ||
    msg.includes('network error') ||
    msg.includes('networkerror') ||
    msg.includes('fetch failed')
  ) {
    return true
  }

  // 2. gRPC статусы UNAVAILABLE (14), DEADLINE_EXCEEDED (4) или abort
  if (code === 'UNAVAILABLE' || code === '14' || code === 'DEADLINE_EXCEEDED' || code === '4' || code === 'CANCELLED') {
    return true
  }

  // 3. HTTP статусы сетевых шлюзов и прокси (502, 503, 504, 521-524)
  if (
    errorObj.status === 502 ||
    errorObj.status === 503 ||
    errorObj.status === 504 ||
    (errorObj.status && errorObj.status >= 521 && errorObj.status <= 524) ||
    msg.includes('502') ||
    msg.includes('503') ||
    msg.includes('504')
  ) {
    return true
  }

  // 4. Потеря тела ответа или трейлеров из-за внезапного обрыва соединения
  if (code === 'INTERNAL' && (msg.includes('missing response body') || msg.includes('missing trailers'))) {
    return true
  }

  return false
}

/**
 * Экспортируем единый экземпляр транспортера для всех gRPC-вызовов
 */
export const grpcTransport = new GrpcWebFetchTransport({
  baseUrl: API_BASE_URL, // начальный динамически разрешённый URL из resolveApiUrls
  format: 'binary', // используем бинарный формат передачи данных

  interceptors: [
    {
      /**
       * Перехватчик unary-вызовов:
       * • для методов telegramLogin и refresh не добавляем токен
       * • для остальных автоматически добавляем accessToken из Pinia store
       * • при сетевом сбое или 502/503/504 автоматически повторяет запрос на резервный URL из списка
       */
      interceptUnary(next, method, input, options) {
        // Методы авторизации и первичных настроек идут без токена
        const noTokenMethods = [
          'telegramLogin',
          'refresh',
          'createLog',
          'signUp',
          'signIn',
          'validateMagicLink',
          'getSettings',
        ]

        let inputWithToken = input
        if (!noTokenMethods.includes(method.name)) {
          const authStore = useAuthStore()
          const accessToken = authStore.accessToken
          inputWithToken = {
            ...input,
            accessToken,
          }
        }

        const allUrls = resolveGrpcUrls()
        if (allUrls.length <= 1) {
          const currentBaseUrl = activeGrpcUrl.value || allUrls[0] || API_BASE_URL
          return next(method, inputWithToken, { ...options, baseUrl: currentBaseUrl })
        }

        const currentActive = activeGrpcUrl.value || allUrls[0]
        let startIndex = allUrls.indexOf(currentActive)
        if (startIndex === -1) startIndex = 0

        const defHeader = new Deferred<RpcMetadata>(true)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const defResponse = new Deferred<any>(true)
        const defStatus = new Deferred<RpcStatus>(true)
        const defTrailer = new Deferred<RpcMetadata>(true)

        let attempt = 0
        const maxAttempts = allUrls.length

        const executeAttempt = (urlIndex: number) => {
          const targetUrl = allUrls[urlIndex]
          const call = next(method, inputWithToken, { ...options, baseUrl: targetUrl })

          call.response
            .then(async response => {
              if (activeGrpcUrl.value !== targetUrl) {
                setActiveGrpcUrl(targetUrl)
              }
              try {
                const header = await call.headers
                defHeader.resolvePending(header)
              } catch {
                defHeader.resolvePending({})
              }
              try {
                const status = await call.status
                defStatus.resolvePending(status)
              } catch {
                defStatus.resolvePending({ code: 'OK', detail: '' })
              }
              try {
                const trailer = await call.trailers
                defTrailer.resolvePending(trailer)
              } catch {
                defTrailer.resolvePending({})
              }
              defResponse.resolvePending(response)
            })
            .catch(err => {
              attempt++
              if (isNetworkOrUnavailableError(err) && attempt < maxAttempts) {
                const nextIndex = (urlIndex + 1) % allUrls.length
                const nextUrl = allUrls[nextIndex]
                console.warn(
                  `[gRPC Failover] Ошибка сети на ${targetUrl}, повторная попытка на ${nextUrl} (попытка ${attempt + 1}/${maxAttempts}):`,
                  err?.message || err,
                )
                executeAttempt(nextIndex)
              } else {
                defHeader.rejectPending(err)
                defResponse.rejectPending(err)
                defStatus.rejectPending(err)
                defTrailer.rejectPending(err)
              }
            })
        }

        executeAttempt(startIndex)

        return new UnaryCall(
          method,
          options.meta ?? {},
          inputWithToken,
          defHeader.promise,
          defResponse.promise,
          defStatus.promise,
          defTrailer.promise,
        )
      },
    },
  ],
})

